import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Person as PersonIcon, 
  Edit as EditIcon,
  SwapHoriz as ConvertIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { Box, IconButton, Typography, Tooltip, Chip } from '@mui/material';

// Import components
import EntityListContainer from '../../components/common/EntityListContainer';
import EntityStatusBadge from '../../components/core/EntityStatusBadge';
import useConfirmDialog from '../../hooks/useConfirmationDialog';

// Import stores
import useAuthStore from '../../stores/authStore';
import useLeadStore from '../../stores/leadStore';
import useNotificationStore from '../../stores/notificationStore';

// Import utils
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/formatters';

// Configuration
const FILTER_CONFIG = [
  {
    name: 'status',
    label: 'Status',
    allLabel: 'All Statuses',
    options: [
      { value: 'new', label: 'New' },
      { value: 'contacted', label: 'Contacted' },
      { value: 'qualified', label: 'Qualified' },
      { value: 'proposal', label: 'Proposal' },
      { value: 'negotiation', label: 'Negotiation' },
      { value: 'won', label: 'Won' },
      { value: 'lost', label: 'Lost' }
    ]
  },
  {
    name: 'source',
    label: 'Source',
    allLabel: 'All Sources',
    options: [
      { value: 'website', label: 'Website' },
      { value: 'social_media', label: 'Social Media' },
      { value: 'referral', label: 'Referral' },
      { value: 'email_campaign', label: 'Email Campaign' },
      { value: 'phone', label: 'Phone' },
      { value: 'event', label: 'Event' },
      { value: 'direct', label: 'Direct' },
      { value: 'other', label: 'Other' }
    ]
  }
];

const SORT_OPTIONS = [
  { field: 'createdAt', label: 'Date Created' },
  { field: 'name', label: 'Name' },
  { field: 'estimatedValue', label: 'Value' },
  { field: 'probability', label: 'Probability' },
  { field: 'status', label: 'Status' }
];

const statusColors = {
  new: 'info',
  contacted: 'primary',
  qualified: 'secondary',
  proposal: 'warning',
  negotiation: 'warning',
  won: 'success',
  lost: 'error'
};

/**
 * Leads listing page
 */
const Leads = () => {
  const navigate = useNavigate();
  
  // Store hooks
  const { hasPermission } = useAuthStore();
  
  // Lead store selectors
  const leads = useLeadStore(state => state.leads);
  const isLoading = useLeadStore(state => state.loading);
  const criticalError = useLeadStore(state => state.criticalError);
  const meta = useLeadStore(state => state.pagination);
  const fetchLeads = useLeadStore(state => state.fetchLeads);
  const deleteLead = useLeadStore(state => state.deleteLead);
  const statistics = useLeadStore(state => state.statistics);
  const { showSuccess, showError } = useNotificationStore();
  
  // Confirmation dialog
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // Check permissions
  const canCreate = hasPermission('leads', 'create');
  const canUpdate = hasPermission('leads', 'update');
  const canDelete = hasPermission('leads', 'delete');
  
  // Handle lead deletion with confirmation
  const handleDeleteLead = useCallback(async (lead) => {
    const confirmed = await confirm({
      title: `Delete ${lead.name}?`,
      content: `This will permanently delete the lead. This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteLead(lead._id);
        showSuccess(`Lead "${lead.name}" deleted successfully`);
      } catch (error) {
        showError(`Failed to delete lead: ${error.message}`);
      }
    }
  }, [confirm, deleteLead, showSuccess, showError]);
  
  // Entity renderer for grid view
  const renderLead = useCallback((lead) => {
    // Safety check
    if (!lead || !lead._id) return null;
    
    return (
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-2px)',
            boxShadow: 2
          }
        }}
        onClick={() => navigate(`/leads/${lead._id}`)}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {lead.name}
          </Typography>
          <Chip
            label={lead.status}
            color={statusColors[lead.status] || 'default'}
            size="small"
          />
        </Box>
        
        {lead.company && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {lead.company}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {lead.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {lead.email}
              </Typography>
            </Box>
          )}
          {lead.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {lead.phone}
              </Typography>
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {lead.estimatedValue && (
              <Box>
                <Typography variant="caption" color="text.secondary">Value</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatCurrency(lead.estimatedValue)}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">Probability</Typography>
              <Typography variant="body2" fontWeight={500}>
                {lead.probability}%
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {canUpdate && lead.status !== 'won' && lead.status !== 'lost' && (
              <Tooltip title="Convert to Client">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/leads/${lead._id}/convert`);
                  }}
                >
                  <ConvertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canUpdate && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/leads/${lead._id}/edit`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteLead(lead);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    );
  }, [canUpdate, canDelete, navigate, handleDeleteLead]);

  // Define table columns
  const tableColumns = [
    { 
      id: 'name', 
      label: 'Lead Name',
      renderCell: (lead) => (
        <Box>
          <Typography variant="body1" fontWeight={600}>
            {lead.name}
          </Typography>
          {lead.company && (
            <Typography variant="caption" color="text.secondary">
              {lead.company}
            </Typography>
          )}
        </Box>
      )
    },
    { 
      id: 'status', 
      label: 'Status', 
      align: 'center',
      renderCell: (lead) => (
        <Chip
          label={lead.status}
          color={statusColors[lead.status] || 'default'}
          size="small"
        />
      )
    },
    { 
      id: 'source', 
      label: 'Source',
      align: 'center',
      renderCell: (lead) => (
        <Typography variant="body2">
          {lead.source.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
        </Typography>
      )
    },
    { 
      id: 'value', 
      label: 'Value',
      align: 'right',
      renderCell: (lead) => (
        lead.estimatedValue ? formatCurrency(lead.estimatedValue) : '-'
      )
    },
    { 
      id: 'probability', 
      label: 'Probability',
      align: 'center',
      renderCell: (lead) => (
        <Typography variant="body2">{lead.probability}%</Typography>
      )
    },
    { 
      id: 'assignedTo', 
      label: 'Assigned To',
      renderCell: (lead) => (
        lead.assignedTo ? 
          `${lead.assignedTo.firstName} ${lead.assignedTo.lastName}` : 
          '-'
      )
    },
    { 
      id: 'lastContact', 
      label: 'Last Contact',
      renderCell: (lead) => (
        lead.lastContactedAt ? formatDate(lead.lastContactedAt) : 'Never'
      )
    }
  ];

  // Configure table action buttons
  const getTableRowActions = useCallback((lead) => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {canUpdate && lead.status !== 'won' && lead.status !== 'lost' && (
          <Tooltip title="Convert to Client">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/leads/${lead._id}/convert`);
              }}
              sx={{ borderRadius: '50%' }}
            >
              <ConvertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {canUpdate && (
          <Tooltip title="Edit">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/leads/${lead._id}/edit`);
              }}
              sx={{ borderRadius: '50%' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        
        {canDelete && (
          <Tooltip title="Delete">
            <IconButton 
              size="small" 
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteLead(lead);
              }}
              sx={{ borderRadius: '50%' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [canUpdate, canDelete, navigate, handleDeleteLead]);
  
  return (
    <>
      <EntityListContainer
        title="Leads"
        description="Manage your sales leads and opportunities"
        fetchData={fetchLeads}
        renderEntity={renderLead}
        initialFilters={{
          status: 'all',
          source: 'all'
        }}
        initialSort={{ field: 'createdAt', direction: 'desc' }}
        filterConfig={FILTER_CONFIG}
        sortOptions={SORT_OPTIONS}
        
        // View options
        availableViews={['grid', 'table']}
        defaultView="table"
        
        // Table configuration
        tableConfig={{
          columns: tableColumns,
          onRowClick: (lead) => navigate(`/leads/${lead._id}`),
          renderRowActions: getTableRowActions
        }}
        
        // Use direct store values
        entities={leads}
        isLoading={isLoading}
        error={null}  // Don't show error - let empty state handle it
        metadata={meta}
        
        actions={{
          canCreate,
          createPath: '/leads/new',
          createLabel: 'New Lead'
        }}
        emptyState={{
          title: 'No Leads Yet',
          description: 'Start building your sales pipeline by adding your first lead.',
          icon: <PersonIcon sx={{ fontSize: 64 }} />,
          getFilteredTitle: () => 'No Matching Leads',
          getFilteredDescription: () => 'Try adjusting your search or filters to find leads.'
        }}
      />
      
      <ConfirmDialog />
    </>
  );
};

export default Leads;